import React, { useState } from 'react';
import confetti from 'canvas-confetti';
import { ToolType } from '../types';

interface ToolCheckoutModalProps {
  toolId: ToolType;
  toolTitle: string;
  isOpen: boolean;
  onClose: () => void;
  onUnlock: (targetId: string, email: string, price: number, planName: string) => void;
  stripePaymentLink?: string;
  initialPlan?: 'single' | 'bundle';
}

export const ToolCheckoutModal: React.FC<ToolCheckoutModalProps> = ({
  toolId,
  toolTitle,
  isOpen,
  onClose,
  onUnlock,
  stripePaymentLink,
  initialPlan = 'single',
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'single' | 'bundle'>(initialPlan);
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const planDetails = {
    single: {
      id: toolId,
      title: `${toolTitle} — Pro License`,
      price: 9,
      description: 'Permanent watermark-free PDF exports and commercial deliverables for this tool.',
    },
    bundle: {
      id: 'all-access-pass',
      title: 'Studio All-Access Pass (All 4 Tools)',
      price: 29,
      description: 'Lifetime pro unlock for all 4 micro-tools, unlimited vector PDF exports, and future additions.',
    },
  };

  const currentPlan = planDetails[selectedPlan];

  const handlePay = () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      alert('Please enter a valid operator email address to register your license key.');
      return;
    }

    setIsProcessing(true);
    setTimeout(() => {
      confetti({
        particleCount: 85,
        spread: 75,
        origin: { y: 0.6 },
        colors: ['#38bdf8', '#10b981', '#f59e0b'],
      });

      onUnlock(currentPlan.id, trimmed, currentPlan.price, currentPlan.title);
      setIsProcessing(false);
      onClose();
    }, 600);
  };

  const handleExternalStripe = () => {
    if (stripePaymentLink && stripePaymentLink.startsWith('http')) {
      window.open(stripePaymentLink, '_blank');
    } else {
      alert(
        'To use an external Stripe Payment Link, configure it in the Terminal Till settings. Instant Direct In-Page Unlock completes immediately in sandbox mode.'
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl blueprint-card p-6 sm:p-7 space-y-6 relative border border-sky-500/40 bg-[#0c0e14]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#8b92a5] hover:text-white font-mono text-lg transition"
          aria-label="Close modal"
        >
          &times;
        </button>

        <div>
          <div className="text-[10px] font-mono text-sky-400 uppercase tracking-widest flex items-center gap-1.5 font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
            INSTANT EDGE UNLOCK // COMMERCIAL LICENSE
          </div>
          <h3 className="font-tech text-2xl font-bold text-white mt-1">
            Unlock Unbranded Deliverables
          </h3>
          <p className="text-xs font-mono text-[#8b92a5] mt-1">
            Export unbranded, high-density vector PDFs and lock in client-facing authority.
          </p>
        </div>

        {/* Plan Selector */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Option A: Single Tool */}
          <div
            onClick={() => setSelectedPlan('single')}
            className={`cursor-pointer p-4 rounded-xl border transition flex flex-col justify-between ${
              selectedPlan === 'single'
                ? 'bg-sky-500/10 border-sky-400 text-white shadow-[0_0_15px_rgba(56,189,248,0.15)]'
                : 'bg-[#10121a] border-[#232736] text-[#8b92a5] hover:border-[#3b4259]'
            }`}
          >
            <div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-mono uppercase tracking-wider font-bold text-sky-300">
                  SINGLE TOOL
                </span>
                <span className="font-mono text-xl font-bold text-white">$9</span>
              </div>
              <h4 className="font-tech text-sm font-bold text-white mt-1">{toolTitle}</h4>
              <p className="text-[10px] font-mono text-[#8b92a5] mt-1 leading-relaxed">
                Watermark-free vector PDF exports for this specific tool.
              </p>
            </div>
            <div className="mt-3 pt-2 border-t border-[#232736] text-[10px] font-mono text-sky-400 font-bold">
              {selectedPlan === 'single' ? '✓ SELECTED' : 'SELECT $9'}
            </div>
          </div>

          {/* Option B: All-Access Pass */}
          <div
            onClick={() => setSelectedPlan('bundle')}
            className={`cursor-pointer p-4 rounded-xl border transition flex flex-col justify-between relative ${
              selectedPlan === 'bundle'
                ? 'bg-emerald-500/10 border-emerald-400 text-white shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                : 'bg-[#10121a] border-[#232736] text-[#8b92a5] hover:border-[#3b4259]'
            }`}
          >
            <div className="absolute -top-2.5 right-3 bg-emerald-400 text-black font-mono text-[10px] font-bold px-2 py-0.5 rounded shadow">
              BEST VALUE
            </div>
            <div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-mono uppercase tracking-wider font-bold text-emerald-400">
                  ALL-ACCESS PASS
                </span>
                <span className="font-mono text-xl font-bold text-white">$29</span>
              </div>
              <h4 className="font-tech text-sm font-bold text-white mt-1">All 4 Micro-Tools</h4>
              <p className="text-[10px] font-mono text-[#8b92a5] mt-1 leading-relaxed">
                Rate Card + Scope Shield + Teardown Lab + Agreement Armor forever.
              </p>
            </div>
            <div className="mt-3 pt-2 border-t border-[#232736] text-[10px] font-mono text-emerald-400 font-bold">
              {selectedPlan === 'bundle' ? '✓ SELECTED' : 'SELECT $29'}
            </div>
          </div>
        </div>

        {/* Form Inputs & Checkout */}
        <div className="space-y-4 font-mono">
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase text-[#8b92a5] flex justify-between">
              <span>Operator Email (For License Receipt)</span>
              <span className="text-zinc-500">Instant Local Sync</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="operator@firm.com"
              className="w-full px-3.5 py-2.5 rounded-lg bg-[#181b26] border border-[#232736] text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-sky-400"
            />
          </div>

          <div className="p-3 rounded-lg bg-[#10121a] border border-[#232736] text-[11px] text-[#8b92a5] space-y-1">
            <div className="text-white font-bold flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              What You Unlock
            </div>
            <p className="text-zinc-400 text-[10px]">
              • 100% unbranded vector PDF generation via browser print engine.
            </p>
            <p className="text-zinc-400 text-[10px]">
              • Unrestricted commercial rights to deploy deliverables with clients.
            </p>
          </div>

          <div className="space-y-2 pt-1">
            <button
              onClick={handlePay}
              disabled={isProcessing}
              className="w-full py-3 bg-sky-400 hover:bg-sky-300 text-black font-tech font-bold text-xs rounded-lg transition shadow-[0_0_15px_rgba(56,189,248,0.2)] disabled:opacity-50"
            >
              {isProcessing
                ? 'ISSUING CRYPTOGRAPHIC LICENSE...'
                : `COMPLETE ORDER & UNLOCK ($${currentPlan.price}.00 USD)`}
            </button>
            <button
              onClick={handleExternalStripe}
              className="w-full py-2 border border-[#232736] hover:border-zinc-500 text-zinc-400 hover:text-white text-[11px] rounded-lg transition"
            >
              Pay with External Stripe Checkout Link &rarr;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

